import React, { useState, useEffect } from 'react';
import BackButton from '../../../ui/BackButton';
import { useBranch } from '../../../layouts/BranchContext'; 
import {
  Button,
  Container,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Modal,
  Snackbar,
  Stack,
  useTheme,
  Checkbox,
  FormControlLabel,
  RadioGroup,
  Radio,
  Tooltip,
  Paper,
  CircularProgress,
} from '@mui/material';
import InfoOutlineIcon from '@mui/icons-material/InfoOutlined';
import {
  Delete as DeleteIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  KeyboardDoubleArrowLeft as ChevronLeft,
  KeyboardDoubleArrowRight as ChevronRight,
  FileUpload as FileUploadIcon,
  FileDownload as FileDownloadIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import MuiAlert from '@mui/material/Alert';
import { styled } from '@mui/system';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const CustomTableScrollbar = styled('div')(({ theme }) => ({
  '&::-webkit-scrollbar': {
    height: '6px',
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
    borderRadius: '3px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
  },
  scrollbarWidth: 'thin',
  scrollbarColor: `${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)'} transparent`,
}));

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const Enquiry = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAddEnquiryModal, setShowAddEnquiryModal] = useState(false);
  const [showEditEnquiryModal, setShowEditEnquiryModal] = useState(false);
  const [showUserGuideModal, setShowUserGuideModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successModalType, setSuccessModalType] = useState('');
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [enquiries, setEnquiries] = useState([]);
  const [filteredEnquiries, setFilteredEnquiries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const rowsPerPageOptions = [5, 10, 25, 50];
  const [showImportModal, setShowImportModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportType, setExportType] = useState('active');
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);

    const { selectedBranchId } = useBranch();
  const organizationId = (() => {
    try {
      return JSON.parse(sessionStorage.getItem('user') || '{}').organization_id || '';
    } catch (e) { return ''; }
  })();

  const [formData, setFormData] = useState({
    sendMail: false,
    sendTextSMS: false,
    corporate: false,
    enquiryCode: '',
    enquiryDate: new Date().toISOString().split('T')[0],
    clientName: '',
    aadharCardNo: '',
    contactPerson: '',
    mobileNo: '',
    alternateMobileNo: '',
    email: '',
    designation: '',
    address: '',
    nextFollowUp: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    preferredTime: '',
    followUpMedium: '',
    enquirySource: '',
    assignedTo: '',
    status: '',
    nationality: '',
    remark: '',
    anyPersonToEnroll: '',
    course: '',
  });

const fetchEnquiries = async (branchId = '') => {
  setLoading(true);
  setError(null);
  try {
    const accessToken = localStorage.getItem('accessToken');
    const sessionUser = JSON.parse(sessionStorage.getItem("user") || "{}");
    const orgId = sessionUser.organization_id;

    const params = new URLSearchParams();
    if (branchId) params.append('branch', branchId);
    if (orgId) params.append('organization', orgId);

    const response = await axios.get(`${API_BASE_URL}/api/student_details/enquiries/?${params.toString()}`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    const data = response.data;
    
    // ✅ FIX: API may return paginated response { count, results } or a plain array
    const dataArray = Array.isArray(data) ? data : (data.results || []);
    
    if (!Array.isArray(dataArray)) {
      throw new Error('API response is not an array');
    }
    
    // Transform API data to match your table structure
    const transformedData = dataArray.map((item) => ({
      enquiryId: item.id,
      enquiryNo: item.enquiry_code,
      enquiryDate: item.enquiry_date,
      candidateName: item.candidate_name,
      mobileNo: item.mobile_no,
      email: item.email,
      enquirySource: item.enquiry_source_name, // Changed from ID to name
      assignedTo: item.assigned_to_name, // Changed from ID to name
      remark: item.remark,
      courseName: item.courses_details?.map(course => course.course_name).join(', ') || '', // Changed to show course names
      clientName: item.candidate_name,
      aadharCardNo: item.aadhar_card_no || '',
      contactPerson: item.candidate_name, // Using candidate_name as contact person
      alternateMobileNo: item.alternate_mobile_no || '',
      designation: '', // Not in API response
      address: item.address || '',
      nextFollowUp: item.next_followup_date || '',
      preferredTime: item.preferred_time || '',
      followUpMedium: item.followup_medium_name, // Changed from ID to name
      status: item.status_name, // Changed from ID to name
      nationality: item.nationality_name, // Changed from ID to name
      anyPersonToEnroll: '', // Not in API response
    }));
    
    setEnquiries(transformedData);
    setFilteredEnquiries(transformedData);
    
    if (transformedData.length === 0) {
      setSnackbarMessage('No enquiries found');
      setSnackbarSeverity('info');
      setOpenSnackbar(true);
    }
  } catch (err) {
    console.error('Error fetching enquiries:', err);
    
    let errorMessage = 'Failed to load enquiries';
    
    if (err.response) {
      errorMessage = `Server error: ${err.response.status} - ${err.response.statusText}`;
      if (err.response.status === 401) {
        errorMessage = 'Unauthorized: Please login again';
      } else if (err.response.status === 404) {
        errorMessage = 'API endpoint not found';
      }
    } else if (err.request) {
      errorMessage = 'Network error: Unable to connect to API';
    } else {
      errorMessage = err.message;
    }
    
    setError(errorMessage);
    setSnackbarMessage(errorMessage);
    setSnackbarSeverity('error');
    setOpenSnackbar(true);
    
    setEnquiries([]);
    setFilteredEnquiries([]);
  } finally {
    setLoading(false);
  }
};

  // Fetch data on component mount
  // ✅ FIX: Re-fetch enquiries whenever selectedBranch changes (branch dropdown switch)
  useEffect(() => {
    fetchEnquiries(selectedBranchId);
  }, [selectedBranchId]);

  // Filter enquiries based on search query
  useEffect(() => {
    if (enquiries.length > 0) {
      setFilteredEnquiries(
        enquiries.filter((enquiry) =>
          enquiry.candidateName.toLowerCase().includes(searchQuery.toLowerCase())
        )
      );
    }
  }, [searchQuery, enquiries]);

  const handleAddEnquiry = () => {
    setFormData({
      sendMail: false,
      sendTextSMS: false,
      corporate: false,
      enquiryCode: '',
      enquiryDate: new Date().toISOString().split('T')[0],
      clientName: '',
      aadharCardNo: '',
      contactPerson: '',
      mobileNo: '',
      alternateMobileNo: '',
      email: '',
      designation: '',
      address: '',
      nextFollowUp: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      preferredTime: '',
      followUpMedium: '',
      enquirySource: '',
      assignedTo: '',
      status: '',
      nationality: '',
      remark: '',
      anyPersonToEnroll: '',
      course: '',
    });
    setShowAddEnquiryModal(true);
  };

  const handleEdit = (enquiry) => {
    setSelectedEnquiry(enquiry);
    setFormData({
      sendMail: false,
      sendTextSMS: false,
      corporate: false,
      enquiryCode: enquiry.enquiryNo,
      enquiryDate: enquiry.enquiryDate,
      clientName: enquiry.clientName,
      aadharCardNo: enquiry.aadharCardNo,
      contactPerson: enquiry.contactPerson,
      mobileNo: enquiry.mobileNo,
      alternateMobileNo: enquiry.alternateMobileNo,
      email: enquiry.email,
      designation: enquiry.designation,
      address: enquiry.address,
      nextFollowUp: enquiry.nextFollowUp,
      preferredTime: enquiry.preferredTime,
      followUpMedium: enquiry.followUpMedium,
      enquirySource: enquiry.enquirySource,
      assignedTo: enquiry.assignedTo,
      status: enquiry.status,
      nationality: enquiry.nationality,
      remark: enquiry.remark,
      anyPersonToEnroll: enquiry.anyPersonToEnroll,
      course: enquiry.courseName,
    });
    setShowEditEnquiryModal(true);
  };

  const handleShowDeleteModal = (enquiry) => {
    setSelectedEnquiry(enquiry);
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setSelectedEnquiry(null);
  };


  const getAccessToken = () =>
    localStorage.getItem('accessToken') ||
    sessionStorage.getItem('accessToken');

  const downloadBlob = (data, fileName) => {
    const url = window.URL.createObjectURL(new Blob([data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleDownloadTemplate = async () => {
    try {
      setDownloadingTemplate(true);
      const response = await axios.get(
        `${API_BASE_URL}/api/student_details/enquiries/import-template/`,
        {
          headers: { Authorization: `Bearer ${getAccessToken()}` },
          responseType: 'blob',
        }
      );
      downloadBlob(response.data, 'enquiry_import_template.xlsx');
      setSnackbarMessage('Template downloaded successfully');
      setSnackbarSeverity('success');
      setOpenSnackbar(true);
    } catch (err) {
      console.error('Enquiry template download failed:', err);
      setSnackbarMessage('Failed to download enquiry template');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
    } finally {
      setDownloadingTemplate(false);
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!/\.(xlsx|xls)$/i.test(file.name)) {
      setSnackbarMessage('Please select an Excel file (.xlsx or .xls)');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
      event.target.value = '';
      return;
    }

    setImportFile(file);
  };

  const closeImportModal = () => {
    setShowImportModal(false);
    setImportFile(null);
  };

  const handleImport = async () => {
    if (!importFile) {
      setSnackbarMessage('Please select an Excel file to import');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
      return;
    }

    try {
      setImporting(true);
      const uploadData = new FormData();
      uploadData.append('file', importFile);

      // Confirm this endpoint and the multipart field name with the backend team.
      const response = await axios.post(
        `${API_BASE_URL}/api/student_details/enquiries/import/excel/`,
        uploadData,
        { headers: { Authorization: `Bearer ${getAccessToken()}` } }
      );

      setSnackbarMessage(
        response.data?.message || 'Enquiries imported successfully'
      );
      setSnackbarSeverity('success');
      setOpenSnackbar(true);
      closeImportModal();
      fetchEnquiries(selectedBranchId);
    } catch (err) {
      console.error('Enquiry import failed:', err);
      const data = err?.response?.data;
      setSnackbarMessage(
        data?.message || data?.error || data?.detail || 'Failed to import enquiries'
      );
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
    } finally {
      setImporting(false);
    }
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      const user = (() => {
        try {
          return JSON.parse(
            sessionStorage.getItem('user') ||
            localStorage.getItem('user') ||
            '{}'
          );
        } catch {
          return {};
        }
      })();

      const orgId = user?.organization_id || organizationId;
      const params = {};
      if (orgId) params.organization_id = orgId;
      if (selectedBranchId) params.branch_id = selectedBranchId;
      if (searchQuery.trim()) params.search = searchQuery.trim();

      // Enquiry export defaults to active records only. Set true only to include inactive records.
      if (exportType === 'all') params.include_inactive = true;

      const response = await axios.get(
        `${API_BASE_URL}/api/student_details/enquiries/export/excel/`,
        {
          headers: { Authorization: `Bearer ${getAccessToken()}` },
          params,
          responseType: 'blob',
        }
      );

      downloadBlob(
        response.data,
        exportType === 'active' ? 'active_enquiries.xlsx' : 'all_enquiries.xlsx'
      );
      setSnackbarMessage('Enquiries exported successfully');
      setSnackbarSeverity('success');
      setOpenSnackbar(true);
      setShowExportModal(false);
    } catch (err) {
      console.error('Enquiry export failed:', err);
      setSnackbarMessage('Failed to export enquiries');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
    } finally {
      setExporting(false);
    }
  };

  const handleDelete = async () => {
    try {
      const accessToken = localStorage.getItem('accessToken');
      
      // Call DELETE API
      await axios.delete(`${API_BASE_URL}/api/student_details/enquiries/${selectedEnquiry.enquiryId}/`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      
      // Update local state
      setEnquiries((prevEnquiries) =>
        prevEnquiries.filter((enq) => enq.enquiryId !== selectedEnquiry.enquiryId)
      );
      
      setSnackbarMessage(`${selectedEnquiry.candidateName} deleted successfully!`);
      setSnackbarSeverity('success');
      setOpenSnackbar(true);
    } catch (err) {
      console.error('Error deleting enquiry:', err);
      setSnackbarMessage('Failed to delete enquiry');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
    } finally {
      setShowDeleteModal(false);
      setSelectedEnquiry(null);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSave = (e) => {
    e.preventDefault();
    if (
      formData.enquiryCode &&
      formData.clientName &&
      formData.contactPerson &&
      formData.mobileNo &&
      formData.email &&
      formData.designation &&
      formData.address &&
      formData.followUpMedium &&
      formData.enquirySource &&
      formData.assignedTo &&
      formData.status &&
      formData.nationality &&
      formData.anyPersonToEnroll &&
      formData.course
    ) {
      const newEnquiry = {
        enquiryId: enquiries.length + 1,
        enquiryNo: formData.enquiryCode,
        enquiryDate: formData.enquiryDate,
        candidateName: formData.clientName,
        mobileNo: formData.mobileNo,
        email: formData.email,
        enquirySource: formData.enquirySource,
        assignedTo: formData.assignedTo,
        remark: formData.remark,
        courseName: formData.course,
        clientName: formData.clientName,
        aadharCardNo: formData.aadharCardNo,
        contactPerson: formData.contactPerson,
        alternateMobileNo: formData.alternateMobileNo,
        designation: formData.designation,
        address: formData.address,
        nextFollowUp: formData.nextFollowUp,
        preferredTime: formData.preferredTime,
        followUpMedium: formData.followUpMedium,
        status: formData.status,
        nationality: formData.nationality,
        anyPersonToEnroll: formData.anyPersonToEnroll,
      };
      setEnquiries([...enquiries, newEnquiry]);
      setShowAddEnquiryModal(false);
      setSuccessModalType('add');
      setShowSuccessModal(true);
      setFormData({
        sendMail: false,
        sendTextSMS: false,
        corporate: false,
        enquiryCode: '',
        enquiryDate: new Date().toISOString().split('T')[0],
        clientName: '',
        aadharCardNo: '',
        contactPerson: '',
        mobileNo: '',
        alternateMobileNo: '',
        email: '',
        designation: '',
        address: '',
        nextFollowUp: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        preferredTime: '',
        followUpMedium: '',
        enquirySource: '',
        assignedTo: '',
        status: '',
        nationality: '',
        remark: '',
        anyPersonToEnroll: '',
        course: '',
      });
    } else {
      setSnackbarMessage('plz fill rquired field!');  
      setOpenSnackbar(true);  
      setSnackbarSeverity("error");
    }
  };

  const handleUpdate = (e) => {
    e.preventDefault();
    if (
      formData.enquiryCode &&
      formData.clientName &&
      formData.contactPerson &&
      formData.mobileNo &&
      formData.email &&
      formData.designation &&
      formData.address &&
      formData.followUpMedium &&
      formData.enquirySource &&
      formData.assignedTo &&
      formData.status &&
      formData.nationality &&
      formData.anyPersonToEnroll &&
      formData.course
    ) {
      const updatedEnquiries = enquiries.map((enq) =>
        enq.enquiryId === selectedEnquiry.enquiryId
          ? {
              ...enq,
              enquiryNo: formData.enquiryCode,
              enquiryDate: formData.enquiryDate,
              candidateName: formData.clientName,
              mobileNo: formData.mobileNo,
              email: formData.email,
              enquirySource: formData.enquirySource,
              assignedTo: formData.assignedTo,
              remark: formData.remark,
              courseName: formData.course,
              clientName: formData.clientName,
              aadharCardNo: formData.aadharCardNo,
              contactPerson: formData.contactPerson,
              alternateMobileNo: formData.alternateMobileNo,
              designation: formData.designation,
              address: formData.address,
              nextFollowUp: formData.nextFollowUp,
              preferredTime: formData.preferredTime,
              followUpMedium: formData.followUpMedium,
              status: formData.status,
              nationality: formData.nationality,
              anyPersonToEnroll: formData.anyPersonToEnroll,
            }
          : enq
      );
      setEnquiries(updatedEnquiries);
      setShowEditEnquiryModal(false);
      setSuccessModalType('edit');
      setShowSuccessModal(true);
      setFormData({
        sendMail: false,
        sendTextSMS: false,
        corporate: false,
        enquiryCode: '',
        enquiryDate: new Date().toISOString().split('T')[0],
        clientName: '',
        aadharCardNo: '',
        contactPerson: '',
        mobileNo: '',
        alternateMobileNo: '',
        email: '',
        designation: '',
        address: '',
        nextFollowUp: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        preferredTime: '',
        followUpMedium: '',
        enquirySource: '',
        assignedTo: '',
        status: '',
        nationality: '',
        remark: '',
        anyPersonToEnroll: '',
        course: '',
      });
      setSelectedEnquiry(null);
    } else {
      setSnackbarMessage('plz fill rquired field!');  
      setOpenSnackbar(true);  
      setSnackbarSeverity("error");
    }
  };

  const handleClear = () => {
    setFormData({
      sendMail: false,
      sendTextSMS: false,
      corporate: false,
      enquiryCode: '',
      enquiryDate: new Date().toISOString().split('T')[0],
      clientName: '',
      aadharCardNo: '',
      contactPerson: '',
      mobileNo: '',
      alternateMobileNo: '',
      email: '',
      designation: '',
      address: '',
      nextFollowUp: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      preferredTime: '',
      followUpMedium: '',
      enquirySource: '',
      assignedTo: '',
      status: '',
      nationality: '',
      remark: '',
      anyPersonToEnroll: '',
      course: '',
    });
  };

  const handleCancel = () => {
    setShowEditEnquiryModal(false);
    setFormData({
      sendMail: false,
      sendTextSMS: false,
      corporate: false,
      enquiryCode: '',
      enquiryDate: new Date().toISOString().split('T')[0],
      clientName: '',
      aadharCardNo: '',
      contactPerson: '',
      mobileNo: '',
      alternateMobileNo: '',
      email: '',
      designation: '',
      address: '',
      nextFollowUp: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      preferredTime: '',
      followUpMedium: '',
      enquirySource: '',
      assignedTo: '',
      status: '',
      nationality: '',
      remark: '',
      anyPersonToEnroll: '',
      course: '',
    });
    setSelectedEnquiry(null);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  const paginatedEnqDetails = filteredEnquiries.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  return (
    <Container sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            variant="outlined"
            placeholder="Search by Candidate Name"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon
                    sx={{
                      color: theme.palette.primary.main,
                      marginLeft: 1.5,
                      fontSize: 22,
                    }}
                  />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setSearchQuery('')}
                    sx={{
                      color: "gray",
                      marginRight: 1,
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
              sx: {
                borderRadius: '30px',
                backgroundColor: theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.15)'
                  : 'rgba(255, 255, 255, 1)',
                boxShadow: theme.palette.mode === 'dark'
                  ? '0 2px 8px rgba(0, 0, 0, 0.3)'
                  : '0 2px 8px rgba(0, 0, 0, 0.1)',
                '& .MuiOutlinedInput-notchedOutline': {
                  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)'}`,
                },
                '&:hover': {
                  backgroundColor: theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.2)'
                    : 'rgba(245, 245, 245, 1)',
                  boxShadow: theme.palette.mode === 'dark'
                    ? '0 2px 12px rgba(0, 0, 0, 0.4)'
                    : '0 2px 12px rgba(0, 0, 0, 0.15)',
                },
                '&.Mui-focused': {
                  backgroundColor: theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.25)'
                    : 'rgba(255, 255, 255, 1)',
                  boxShadow: `0 0 0 3px ${theme.palette.primary.main}20`,
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: `2px solid ${theme.palette.primary.main}`,
                  },
                },
                paddingLeft: 1.5,
                height: '50px',
                transition: 'all 0.3s ease',
              },
            }}
            sx={{
              width: 300,
              maxWidth: '100%',
              '& .MuiInputBase-input': {
                padding: '10px 14px',
                color: theme.palette.text.primary,
                fontWeight: 500,
              },
            }}
          />

          <Tooltip title="Import Enquiries" arrow>
            <IconButton
              onClick={() => setShowImportModal(true)}
              sx={{
                width: 50,
                height: 50,
                backgroundColor: theme.palette.warning.main,
                color: '#fff',
                '&:hover': { backgroundColor: theme.palette.warning.dark },
              }}
            >
              <FileUploadIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Export Enquiries" arrow>
            <IconButton
              onClick={() => setShowExportModal(true)}
              sx={{
                width: 50,
                height: 50,
                backgroundColor: theme.palette.info.main,
                color: '#fff',
                '&:hover': { backgroundColor: theme.palette.info.dark },
              }}
            >
              <FileDownloadIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            onClick={() => navigate('/add-enquiry')}
            sx={{
              width: 160,
              height: 50,
              fontWeight: 'bold',
              backgroundColor: theme.palette.primary.main,
              '&:hover': { backgroundColor: theme.palette.primary.dark },
              borderRadius: '10px',
            }}
            startIcon={<AddIcon />}
          >
            NEW ENQUIRY
          </Button>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Tooltip title="User Guide" arrow>
          <IconButton
            onClick={() => setShowUserGuideModal(true)}
            sx={{
              width: 60,
              height: 60,
              borderRadius: '50%',
              minWidth: 0,
            }}
          >
            <InfoOutlineIcon sx={{ fontSize: 30 }} />
          </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      )}

      {/* Error State */}
      {error && !loading && (
        <Box sx={{ mb: 2 }}>
          <Alert 
            severity="error" 
            action={
              <Button 
                color="inherit" 
                size="small" 
                onClick={() => fetchEnquiries(selectedBranchId)}
                sx={{ fontWeight: 'bold' }}
              >
                RETRY
              </Button>
            }
          >
            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
              {error}
            </Typography>
          </Alert>
        </Box>
      )}

      {/* Table */}
      {!loading && (
        <Paper
          elevation={3}
          sx={{
            paddingBottom: 0,
            maxWidth: '100%',
            boxSizing: 'border-box',
            mx: 'auto',
            backgroundColor: theme.palette.background.paper,
            borderRadius: '16px',
          }}
        >
          <CustomTableScrollbar sx={{ width: '100%', overflowX: 'auto' }}>
            <TableContainer sx={{ width: '100%', borderRadius: '16px' }}>
              <Table
                sx={{
                  minWidth: 1200,
                  '& .MuiTableHead-root .MuiTableCell-root': {
                    fontWeight: 'bold',
                    backgroundColor: theme.palette.primary.main,
                    color: 'white',
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                      <TableCell sx={{ fontSize: '1.1rem', minWidth: 70, whiteSpace: 'nowrap' }}>S.No</TableCell>
                      <TableCell sx={{ fontSize: '1.1rem', minWidth: 120, whiteSpace: 'nowrap' }}>Enquiry No.</TableCell>
                    <TableCell sx={{ fontSize: '1.1rem', minWidth: 120, whiteSpace: 'nowrap' }}>Enquiry Date</TableCell>
                    <TableCell sx={{ fontSize: '1.1rem', minWidth: 150, whiteSpace: 'nowrap' }}>Candidate Name</TableCell>
                    <TableCell sx={{ fontSize: '1.1rem', minWidth: 120, whiteSpace: 'nowrap' }}>Mobile No.</TableCell>
                    <TableCell sx={{ fontSize: '1.1rem', minWidth: 150, whiteSpace: 'nowrap' }}>Email</TableCell>
                    <TableCell sx={{ fontSize: '1.1rem', minWidth: 120, whiteSpace: 'nowrap' }}>Enquiry Source</TableCell>
                    <TableCell sx={{ fontSize: '1.1rem', minWidth: 120, whiteSpace: 'nowrap' }}>Assigned To</TableCell>
                    <TableCell sx={{ fontSize: '1.1rem', minWidth: 150, whiteSpace: 'nowrap' }}>Remark</TableCell>
                    <TableCell sx={{ fontSize: '1.1rem', minWidth: 150, whiteSpace: 'nowrap' }}>Course Name</TableCell>
                    <TableCell align="center" sx={{ fontSize: '1.1rem', minWidth: 120, whiteSpace: 'nowrap', backgroundColor: theme.palette.primary.dark }}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedEnqDetails.length > 0 ? (
                    paginatedEnqDetails.map((enquiry, index) => (
                      <TableRow
                        key={enquiry.enquiryId}
                        sx={{
                          backgroundColor: index % 2 === 0 ? theme.palette.background.paper : theme.palette.background.paper,
                          '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                        }}
                      >
                          <TableCell sx={{ fontSize: '1rem', whiteSpace: 'nowrap' }}>{(page - 1) * rowsPerPage + index + 1}</TableCell>
                          <TableCell sx={{ fontSize: '1rem', whiteSpace: 'nowrap' }}>{enquiry.enquiryNo}</TableCell>
                        <TableCell sx={{ fontSize: '1rem', whiteSpace: 'nowrap' }}>{enquiry.enquiryDate}</TableCell>
                        <TableCell sx={{ fontSize: '1rem', whiteSpace: 'nowrap' }}>{enquiry.candidateName}</TableCell>
                        <TableCell sx={{ fontSize: '1rem', whiteSpace: 'nowrap' }}>{enquiry.mobileNo}</TableCell>
                        <TableCell sx={{ fontSize: '1rem', whiteSpace: 'nowrap' }}>{enquiry.email}</TableCell>
                        <TableCell sx={{ fontSize: '1rem', whiteSpace: 'nowrap' }}>{enquiry.enquirySource}</TableCell>
                        <TableCell sx={{ fontSize: '1rem', whiteSpace: 'nowrap' }}>{enquiry.assignedTo}</TableCell>
                        <TableCell sx={{ fontSize: '1rem', whiteSpace: 'nowrap' }}>{enquiry.remark}</TableCell>
                        <TableCell sx={{ fontSize: '1rem', whiteSpace: 'nowrap' }}>{enquiry.courseName}</TableCell>
                        <TableCell align="center">
                          <Tooltip title="Edit" arrow>
                          <IconButton
                            onClick={() => navigate(`/edit-enquiry/${enquiry.enquiryId}`)}
                            sx={{
                              color: theme.palette.primary.light,
                              '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                            }}
                          >
                            <EditIcon />
                          </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete" arrow>
                          <IconButton
                            color="error"
                            onClick={() => handleShowDeleteModal(enquiry)}
                            sx={{
                              '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow sx={{
                      backgroundColor: theme.palette.background.paper,
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                      },
                    }}>
                      <TableCell colSpan={11} align="center" sx={{ fontSize: '1rem', padding: '20px', wordSpacing: 7 }}>
                        No Data Found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CustomTableScrollbar>
        </Paper>
      )}

      {filteredEnquiries.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1, p: 2, pt: 2 }}>
          <Typography variant="body2" color={theme.palette.primary.main}>
            Page <strong>{page}</strong> of <strong>{Math.ceil(filteredEnquiries.length / rowsPerPage)}</strong>
          </Typography>
          <Stack direction="row" alignItems="center" spacing={1}>
            <IconButton
              onClick={() => handleChangePage(null, page - 1)}
              disabled={page === 1}
              sx={{
                color: theme.palette.primary.main,
                '&:hover': { backgroundColor: theme.palette.action.hover },
                '&.Mui-disabled': { opacity: 0.3 },
                borderRadius: '50%',
                width: '36px',
                height: '36px',
              }}
            >
              <ChevronLeft />
            </IconButton>
            {[...Array(Math.ceil(filteredEnquiries.length / rowsPerPage))].map((_, index) => (
              <IconButton
                key={index + 1}
                onClick={() => handleChangePage(null, index + 1)}
                sx={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  backgroundColor: page === index + 1 ? theme.palette.primary.main : 'transparent',
                  color: page === index + 1 ? theme.palette.primary.contrastText : theme.palette.text.primary,
                  '&:hover': {
                    backgroundColor: page === index + 1 ? theme.palette.primary.dark : theme.palette.action.hover,
                  },
                }}
              >
                {index + 1}
              </IconButton>
            ))}
            <IconButton
              onClick={() => handleChangePage(null, page + 1)}
              disabled={page >= Math.ceil(filteredEnquiries.length / rowsPerPage)}
              sx={{
                color: theme.palette.primary.main,
                '&:hover': { backgroundColor: theme.palette.action.hover },
                '&.Mui-disabled': { opacity: 0.3 },
                borderRadius: '50%',
                width: '36px',
                height: '36px',
              }}
            >
              <ChevronRight />
            </IconButton>
            <FormControl size="small" sx={{ minWidth: 100, ml: 1 }}>
              <InputLabel id="rows-per-page-label" sx={{ color: theme.palette.text.primary }}>Rows</InputLabel>
              <Select
                labelId="rows-per-page-label"
                value={rowsPerPage}
                onChange={handleChangeRowsPerPage}
                label="Rows"
                sx={{
                  height: '36px',
                  color: theme.palette.text.primary,
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.text.primary },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main },
                }}
              >
                {rowsPerPageOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </Box>
      )}

      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>


      {/* Import Enquiries Modal - same layout as Admission */}
      <Modal
        open={showImportModal}
        onClose={() => { if (!importing) closeImportModal(); }}
        BackdropProps={{ style: { backgroundColor: 'rgba(0,0,0,0.8)' } }}
      >
        <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 480, maxWidth: '90vw', bgcolor: 'background.paper', borderRadius: 2, boxShadow: 24, overflow: 'hidden', maxHeight: '90vh' }}>
          <Box sx={{ backgroundColor: theme.palette.primary.main, padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ color: '#fff', fontWeight: 'bold' }}>Import Enquiries</Typography>
            <IconButton onClick={closeImportModal} disabled={importing} sx={{ color: '#fff' }}><CloseIcon /></IconButton>
          </Box>
          <Box sx={{ padding: '20px', overflowY: 'auto', maxHeight: 'calc(90vh - 60px)' }}>
            <Typography sx={{ fontWeight: 'bold', mb: 1 }}>Step 1: Download the template</Typography>
            <Button variant="outlined" startIcon={downloadingTemplate ? <CircularProgress size={18} /> : <DownloadIcon />} onClick={handleDownloadTemplate} disabled={downloadingTemplate} sx={{ mb: 3 }}>
              {downloadingTemplate ? 'Downloading...' : 'Download Template'}
            </Button>
            <Typography sx={{ fontWeight: 'bold', mb: 0.5 }}>Step 2: Enter data in the proper format</Typography>
            <Typography variant="body2" sx={{ mb: 3 }}>Fill in the enquiry details in the downloaded Excel file. Keep the template column headings unchanged. Related fields should contain IDs; course_ids accepts comma-separated course IDs.</Typography>
            <Typography sx={{ fontWeight: 'bold', mb: 1 }}>Step 3: Choose the Excel file</Typography>
            <Button variant="contained" component="label" sx={{ mb: 1 }} disabled={importing}>
              Select File
              <input type="file" hidden accept=".xlsx,.xls" onChange={handleFileChange} onClick={(event) => { event.target.value = null; }} />
            </Button>
            {importFile && <Typography variant="body2" sx={{ mb: 1, overflowWrap: 'anywhere' }}>Selected: {importFile.name}</Typography>}
            <Typography sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>Step 4: Click Import</Typography>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
              <Button variant="outlined" color="error" onClick={closeImportModal} disabled={importing} data-app-cancel="true">Cancel</Button>
              <Button variant="contained" onClick={handleImport} disabled={!importFile || importing} startIcon={importing ? <CircularProgress size={18} color="inherit" /> : null}>
                {importing ? 'Importing...' : 'Import'}
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* Export Enquiries Modal - same layout as Admission */}
      <Modal open={showExportModal} onClose={() => { if (!exporting) setShowExportModal(false); }} BackdropProps={{ style: { backgroundColor: 'rgba(0,0,0,0.8)' } }}>
        <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 400, maxWidth: '90vw', bgcolor: 'background.paper', borderRadius: 2, boxShadow: 24, overflow: 'hidden' }}>
          <Box sx={{ backgroundColor: theme.palette.primary.main, padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ color: '#fff', fontWeight: 'bold' }}>Export Enquiries</Typography>
            <IconButton onClick={() => setShowExportModal(false)} disabled={exporting} sx={{ color: '#fff' }}><CloseIcon /></IconButton>
          </Box>
          <Box sx={{ padding: '20px' }}>
            <Typography sx={{ mb: 1, fontWeight: 'bold' }}>Which enquiries do you want to export?</Typography>
            <RadioGroup value={exportType} onChange={(event) => setExportType(event.target.value)}>
              <FormControlLabel value="active" control={<Radio />} label="Active records only" />
              <FormControlLabel value="all" control={<Radio />} label="All records (active + inactive)" />
            </RadioGroup>
            <Typography variant="body2" sx={{ mt: 1, color: 'text.primary' }}>The current organization, selected branch, and search filters will be applied where supported.</Typography>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3 }}>
              <Button variant="outlined" color="error" onClick={() => setShowExportModal(false)} disabled={exporting} data-app-cancel="true">Cancel</Button>
              <Button variant="contained" onClick={handleExport} disabled={exporting} startIcon={exporting ? <CircularProgress size={18} color="inherit" /> : <FileDownloadIcon />}>
                {exporting ? 'Exporting...' : 'Export'}
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* Delete Modal */}
      <Modal
        open={showDeleteModal}
        onClose={handleCloseDeleteModal}
        BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.8)' } }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ backgroundColor: theme.palette.primary.main, padding: '12px 16px' }}>
            <Typography id="delete-modal-title" variant="h6" sx={{ color: '#fff' }}>
              Delete Confirmation ⚠️
            </Typography>
          </Box>
          <Box sx={{ padding: '20px' }}>
            <Typography id="delete-modal-description" sx={{ mb: 2 }}>
              Are you sure you want to delete <strong>{selectedEnquiry?.candidateName}</strong>?
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button onClick={handleCloseDeleteModal} variant="outlined" sx={{ mr: 1 }} data-app-cancel="true">
                Cancel
              </Button>
              <Button variant="contained" color="error" onClick={handleDelete}>
                Yes, Delete
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* User Guide Modal */}
      <Modal
        open={showUserGuideModal}
        onClose={() => setShowUserGuideModal(false)}
        BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.8)' } }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 800,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              backgroundColor: theme.palette.primary.main,
              padding: '12px 16px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              cursor: 'pointer',
              position: 'relative',
            }}
          >
            <Typography variant="h4" sx={{ color: '#fff', fontWeight: 'bold' }}>
              Enquiry Module - User Guide
            </Typography>
            <IconButton
              onClick={() => setShowUserGuideModal(false)}
              sx={{ color: '#fff', position: 'absolute', right: 14 }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <CustomTableScrollbar sx={{ maxHeight: '70vh', overflowY: 'auto', padding: '20px' }}>
            <Typography
              variant="body1"
              sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mb: 1 }}
            >
              ➤ Follow these steps to add a new enquiry:
            </Typography>
            <ul style={{ marginLeft: '20px', lineHeight: '1.8', cursor: 'pointer' }}>
              <li>Click the <strong>NEW ENQUIRY</strong> button at the top right to open the enquiry form.</li>
              <li>Fill in all required fields: Enquiry Code, Client Name, Contact Person, Mobile No., Email, Designation, Address, Follow Up Medium, Enquiry Source, Assigned To, Status, Nationality, Any Person to Enroll, and Course.</li>
              <li>Optionally, select <strong>Send Mail</strong>, <strong>Send Text SMS</strong>, or <strong>Corporate</strong> as needed.</li>
              <li>Select options from dropdowns for Follow Up Medium, Enquiry Source, Assigned To, Status, Nationality, and Course.</li>
              <li>Enter additional details like Aadhar Card No., Alternate Mobile No., Remark, Next Follow Up date, and Preferred Time if applicable.</li>
              <li>Review all entered information for accuracy.</li>
              <li>Click the <strong>SAVE</strong> button to save the enquiry. A success message will confirm the addition.</li>
              <li>The new enquiry will appear in the Enquiry List table.</li>
            </ul>
            <Typography variant="body2" sx={{ mt: 2, color: theme.palette.text.primary }}>
              ⚠️ Note: All fields marked with * are mandatory. Ensure they are filled to avoid submission errors.
            </Typography>

            <Typography
              variant="body1"
              sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mt: 3, mb: 1 }}
            >
              ➤ Follow these steps to edit an existing enquiry:
            </Typography>
            <ul style={{ marginLeft: '20px', lineHeight: '1.8' }}>
              <li>Select the enquiry you want to update from the Enquiry List table.</li>
              <li>Click the <strong>Edit</strong> (pencil icon) in the Action column to open the edit form.</li>
              <li>Modify the necessary fields, ensuring all mandatory fields are filled.</li>
              <li>Click the <strong>UPDATE</strong> button to save changes. A success message will confirm the update.</li>
              <li>To discard changes, click <strong>CANCEL</strong>.</li>
            </ul>
            <Typography variant="body2" sx={{ mt: 2, color: theme.palette.text.primary }}>
              ⚠️ Note: Ensure all mandatory fields are filled to avoid submission errors.
            </Typography>

            <Typography
              variant="body1"
              sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mt: 3, mb: 1 }}
            >
              ➤ Follow these steps to delete an enquiry:
            </Typography>
            <ul style={{ marginLeft: '20px', lineHeight: '1.8' }}>
              <li>Select the enquiry you want to delete from the Enquiry List table.</li>
              <li>Click the <strong>Delete</strong> (trash icon) in the Action column.</li>
              <li>Confirm the deletion in the modal by clicking <strong>Yes, Delete</strong>.</li>
              <li>The enquiry will be removed, and a success message will appear.</li>
            </ul>
            <Typography variant="body2" sx={{ mt: 2, color: theme.palette.text.primary }}>
              ⚠️ Note: Deleted enquiries cannot be recovered. Proceed with caution.
            </Typography>

            <Typography
              variant="body1"
              sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mt: 3, mb: 1 }}
            >
              ➤ Follow these steps to search for an enquiry:
            </Typography>
            <ul style={{ marginLeft: '20px', lineHeight: '1.8' }}>
              <li>Use the search bar at the top left of the Enquiry List.</li>
              <li>Enter the candidate's name to filter the table.</li>
              <li>The table will update automatically to show matching enquiries.</li>
            </ul>
            <Typography variant="body2" sx={{ mt: 2, color: theme.palette.text.primary }}>
              ⚠️ Note: The search is case-insensitive and filters by Candidate Name.
            </Typography>

            <Typography
              variant="body1"
              sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mt: 3, mb: 1 }}
            >
              ➤ Follow these steps to import or export enquiries:
            </Typography>
            <ul style={{ marginLeft: '20px', lineHeight: '1.8' }}>
              <li>To import enquiries, click <strong>IMPORT</strong> and follow the prompts to upload a compatible file.</li>
              <li>To export enquiries, click <strong>EXPORT</strong> to download the data in a suitable format.</li>
            </ul>
            <Typography variant="body2" sx={{ mt: 2, color: theme.palette.text.primary }}>
              ⚠️ Note: Ensure the import file format matches the system's requirements to avoid errors.
            </Typography>

            <Typography
              variant="body1"
              sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mt: 3, mb: 1 }}
            >
              ➤ Follow these steps to navigate the enquiry list:
            </Typography>
            <ul style={{ marginLeft: '20px', lineHeight: '1.8' }}>
              <li>Use the pagination controls at the bottom to browse pages.</li>
              <li>Click the left (<strong>&lt;</strong>) or right (<strong>&gt;</strong>) arrows to switch pages.</li>
              <li>Select a specific page number by clicking on it.</li>
              <li>Adjust the number of rows per page using the dropdown menu (e.g., 5, 10, 25, 50 rows).</li>
            </ul>
            <Typography variant="body2" sx={{ mt: 2, color: theme.palette.text.primary }}>
              ⚠️ Note: The table displays only the selected number of rows per page.
            </Typography>
          </CustomTableScrollbar>
        </Box>
      </Modal>
    </Container>
  );
};

export default Enquiry;